import { useQuery } from '@tanstack/react-query'
import { createRoute } from '@tanstack/react-router'
import { EmptyState, MutedText } from '@volee/ui'

import { canSignOut, meQueryOptions, userOf } from '../auth/me.ts'
import { messages } from '../messages/index.ts'
import { interpolate } from '../messages/interpolate.ts'
import { SignInButton, SignOutButton } from '../components/session-buttons.tsx'
import { DevBypassBadge } from '../components/session-identity.tsx'
import { parseOptionalOneOf } from './params.ts'
import { rootRoute } from './root.tsx'

// Where a valid session with no way in ends up, the common case here. Distinct from `/sign-in`,
// which for such a session is a loop. Cases: apps/admin-panel/docs/401-e-403-risposte-diverse.md

// `admin-required` is its own reason and not `no-role`: that one says the profile has no
// access to a *section*, which is false for an operations account that uses /staff daily.
const FORBIDDEN_REASONS = [
  'no-role',
  'admin-required',
  'not-linked',
  'deactivated',
  'refused',
] as const

export type ForbiddenReason = (typeof FORBIDDEN_REASONS)[number]

export interface ForbiddenSearch {
  readonly reason?: ForbiddenReason
}

/** Accepts only a reason the guard sends: the value selects text shown to the user. */
export function parseForbiddenReason(value: unknown): ForbiddenReason | undefined {
  return parseOptionalOneOf(FORBIDDEN_REASONS, value)
}

export const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forbidden',
  validateSearch: (search: Record<string, unknown>): ForbiddenSearch => ({
    reason: parseForbiddenReason(search.reason),
  }),
  // Reached without the back office being usable: no frame around it (see `chrome`).
  staticData: { chrome: 'bare' },
  component: Forbidden,
})

// Annotated here: this is the line that stops compiling when a reason gains no sentence.
const REASON_TEXT: Record<ForbiddenReason, string> = messages.forbidden.reason

function Forbidden() {
  const { reason } = forbiddenRoute.useSearch()
  const { data: session } = useQuery(meQueryOptions)
  const me = userOf(session)

  /** The query decides; `?reason=` is a shareable hint, and it is user-typed. */
  function reasonToShow(): ForbiddenReason {
    if (session?.kind === 'refused') return session.reason
    return reason ?? 'no-role'
  }

  /** Naming the account matters more than the refusal: the likeliest cause is the wrong one. */
  function description() {
    const refusal = REASON_TEXT[reasonToShow()]
    if (me === undefined) return refusal
    return `${refusal} ${interpolate(messages.forbidden.signedInAs, { email: me.email })}`
  }

  // No way "home": for whoever reads this, the guard would answer that link by sending them
  // back here.
  function action() {
    if (canSignOut(session)) return <SignOutButton />
    if (session?.kind === 'anonymous') return <SignInButton returnTo="/" />
    // The local dev bypass: no session to end and nowhere to go, so the page says why rather
    // than leaving a dead end. The fix is a `volee_staff` row or another `VOLEE_DEV_USER`.
    if (me?.is_dev_bypass === true) {
      return (
        <div className="flex flex-col items-center gap-2">
          <DevBypassBadge />
          <MutedText>{messages.session.devBypassHint}</MutedText>
        </div>
      )
    }
    return undefined
  }

  return (
    <EmptyState title={messages.forbidden.title} description={description()} action={action()} />
  )
}
